import {
	Component,
	inject,
	Input,
	input,
	OnChanges,
	OnInit,
	output,
	SimpleChanges,
	ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Selection } from "common/selection.service";
import { assertDefined } from "core/functions";
import { NgClass } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

export interface TableColumn {
	name: string;
	columnClass?: string;
	sortable?: boolean;
	initialSortOrder: SortOrder;
	optional?: boolean;
}

export interface TableCell {
	text: string | null;
}

export interface TableRow {
	iconClass?: string;
	disabled: boolean;
	clickable: boolean;
	checkItem: boolean;
	id: string;
}

export interface SortEvent {
	sortKey: string;
	sortOrder: SortOrder;
}

export enum SortOrder {
	Undefined,
	Ascending,
	Descending,
}

@Component({
	selector: "stages-table-viewer",
	templateUrl: "table-viewer.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, ReactiveFormsModule, FormsModule, TranslateModule],
})
export class TableViewerComponent<T> implements OnInit, OnChanges {
	private translateService = inject(TranslateService);

	sortOrder: Record<string, SortOrder> = {};

	readonly columns = input.required<TableColumn[]>();

	readonly items = input.required<T[]>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input() rowProvider!: (item: T) => TableRow;

	readonly cellProvider = input.required<(item: T, index: number) => TableCell>();

	readonly columnNamePrefix = input.required<string>();

	readonly translateNone = input.required<string>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	selection!: Selection<T>;

	readonly selectionChanged = output<void>();

	readonly sortOrderChanged = output<SortEvent>();

	readonly itemClicked = output<T>();

	allSelected = false;

	ngOnInit(): void {
		const columns = this.columns();
		assertDefined(columns);
		assertDefined(this.items());
		assertDefined(this.rowProvider);
		assertDefined(this.cellProvider());
		assertDefined(this.columnNamePrefix());
		assertDefined(this.translateNone());
		assertDefined(this.selection);

		columns.forEach((column) => (this.sortOrder[column.name] = column.initialSortOrder));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.selection) {
			this.onSelect();
		}
	}

	sort(key: string): void {
		this.columns().forEach((column) => {
			if (column.name === key) {
				this.sortOrder[column.name] =
					this.sortOrder[column.name] === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending;
				this.sortOrderChanged.emit({ sortKey: column.name, sortOrder: this.sortOrder[column.name] });
			} else {
				this.sortOrder[column.name] = SortOrder.Undefined;
			}
		});
	}

	getRowIconClasses(item: T): string[] {
		const iconClasses: string[] = [];
		const row: TableRow = this.rowProvider(item);
		if (row.iconClass) {
			iconClasses.push(row.iconClass);
		}
		if (row.clickable) {
			iconClasses.push("clickable");
		}
		return iconClasses;
	}

	onSelectAllToggled(): void {
		if (this.selection.areAllSelected()) {
			this.allSelected = false;
			this.selection.selectNone();
		} else {
			this.allSelected = true;
			this.selection.selectAll();
		}
		// TODO: The 'emit' function requires a mandatory void argument
		this.selectionChanged.emit();
	}

	getSelectCheckboxTitle(): string {
		return this.allSelected
			? this.translateService.instant("select.none")
			: this.translateService.instant("select.all");
	}

	isClickHandled(item: T): boolean {
		const row: TableRow = this.rowProvider(item);
		return !row.checkItem && row.clickable && !row.disabled;
	}

	isSortedBy(column: TableColumn, expected: SortOrder): boolean {
		return expected === this.sortOrder[column.name];
	}

	isDisplayingMessageWhenEmpty(): boolean {
		const items = this.items();
		return items && items.length === 0;
	}

	isSelectAllDisplayed(): boolean {
		const items = this.items();
		return (
			items &&
			items.some((item) => {
				const row: TableRow = this.rowProvider(item);
				return row.checkItem && !row.disabled;
			})
		);
	}

	onSelect(): void {
		this.allSelected = this.selection.areAllSelected();
		// TODO: The 'emit' function requires a mandatory void argument
		this.selectionChanged.emit();
	}
}
